import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  LinearProgress, Stack, TextField, Typography } from '@mui/material';
import { ScienceOutlined, SendOutlined } from '@mui/icons-material';
import { apiService, API_ENDPOINTS } from '../../config/api';
import type { DashboardUpdate } from '../../types';

type Preview = {
  count: number; recipients: { email: string; name: string }[]; invalidEmails: number; reviewToken: string;
  message: { subject: string; text: string }; delivery: { ready: boolean; reason: string | null; sender: string | null };
};
type Broadcast = { id: string; total: number; accepted: number; unconfirmed: number; skipped: number;
  remaining: number; inProgress: number; state: string; busyUntil: number };

export default function DeveloperUpdateEmailDialog({ update, onClose }: { update: DashboardUpdate; onClose: () => void }) {
  const base = API_ENDPOINTS.DASHBOARD_UPDATES.DETAIL(update.id);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [broadcast, setBroadcast] = useState<Broadcast | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const stop = useRef(false);
  useEffect(() => {
    let active = true;
    apiService.get(`${base}/email-status`).then(result => { if (active) setBroadcast(result.broadcast); })
      .catch(() => { if (active) setError('Could not check previous sends. Retry the dry run.'); });
    return () => { active = false; stop.current = true; };
  }, [base]);

  const dryRun = async () => {
    setBusy(true); setError(''); setNotice(''); setConfirmed(false); setPreview(null);
    try {
      const result: Preview = await apiService.post(`${base}/email-preview`);
      setPreview(result);
      const status = await apiService.get(`${base}/email-status`); setBroadcast(status.broadcast);
      setNotice('Dry run complete. No emails sent and no send job created.');
    } catch (e: any) { setError(e.message || 'Could not prepare the dry run.'); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!preview) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await apiService.post(`${base}/email-test`, { reviewToken: preview.reviewToken });
      setNotice(`Test email accepted by the email provider for ${result.recipient}. Check your inbox.`);
    } catch (e: any) { setError(e.message || 'The test could not be sent.'); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true); setSending(true); setError(''); setNotice(''); stop.current = false;
    try {
      let job = broadcast;
      if (!job) {
        if (!preview || !confirmed) return;
        job = (await apiService.post(`${base}/email-send`, { reviewToken: preview.reviewToken,
          confirmedRecipientCount: preview.count, confirm: true })).broadcast as Broadcast;
        setBroadcast(job);
      }
      while (job.state !== 'complete' && !stop.current) {
        job = (await apiService.post(`${base}/email-broadcasts/${job.id}/continue`)).broadcast as Broadcast;
        setBroadcast(job);
      }
      setNotice(job.state === 'complete' ? 'Broadcast finished. Provider acceptance does not guarantee inbox delivery.' : 'Paused. You can resume the remaining recipients here.');
    } catch (e: any) { setError(e.message || 'Sending paused. Refresh the dry run to check progress before resuming.'); }
    finally { setBusy(false); setSending(false); }
  };
  const settled = broadcast ? broadcast.accepted + broadcast.unconfirmed + broadcast.skipped : 0;
  const recipients = preview?.recipients.filter(item => `${item.name} ${item.email}`.toLowerCase().includes(search.toLowerCase())) || [];

  return <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="md" aria-labelledby="developer-email-title">
    <DialogTitle id="developer-email-title">Email approved researchers
      <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>Participants are excluded. Each researcher receives a separate email.</Typography>
    </DialogTitle>
    <DialogContent dividers sx={{ bgcolor: '#f7f8fa' }}>
      <Stack gap={2}>
        {error && <Alert severity="error">{error}</Alert>}
        {notice && <Alert severity="success">{notice}</Alert>}
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <Button variant="outlined" startIcon={<ScienceOutlined />} disabled={busy} onClick={dryRun}>Run dry run</Button>
          <Typography variant="body2" color="text.secondary">Preview the message and exact recipient list before sending.</Typography>
        </Stack>
        {busy && <LinearProgress aria-label={sending ? 'Sending email batch' : 'Preparing email preview'} />}
        {preview && <>
          {!preview.delivery.ready && <Alert severity="warning">{preview.delivery.reason} The dry run still works.</Alert>}
          <Box sx={{ p: 2.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">From {preview.delivery.sender || '(sender not configured)'}</Typography>
            <Typography fontWeight={600} sx={{ mt: .5, mb: 1.5 }}>{preview.message.subject}</Typography>
            {update.imageUrl && <Box component="img" src={update.imageUrl} alt="Update attachment" sx={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain', mb: 1 }} />}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.8 }}>{preview.message.text}</Typography>
          </Box>
          <Box sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography fontWeight={600} sx={{ mb: 1 }}>{preview.count} approved researchers · Duplicate addresses removed</Typography>
            {preview.invalidEmails > 0 && <Alert severity="warning" sx={{ mb: 1 }}>{preview.invalidEmails} approved profiles have no valid email address and will be skipped.</Alert>}
            <TextField size="small" fullWidth label="Find a recipient" value={search} onChange={event => setSearch(event.target.value)} />
            <Box component="ul" aria-label="Reviewed email recipients" sx={{ maxHeight: 180, overflow: 'auto', pl: 2.5, mb: 0 }}>
              {recipients.map(item => <Typography component="li" variant="body2" key={item.email} sx={{ py: .5, overflowWrap: 'anywhere' }}>
                {item.name ? `${item.name} · ` : ''}{item.email}
              </Typography>)}
            </Box>
            {preview.count === 0 && <Typography variant="body2">No eligible recipients.</Typography>}
          </Box>
          {!broadcast && <FormControlLabel control={<Checkbox checked={confirmed} disabled={busy} onChange={event => setConfirmed(event.target.checked)} />}
            label={`I reviewed this message and the ${preview.count} recipients.`} />}
        </>}
        {broadcast && <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2 }}>
          <Typography fontWeight={600}>{broadcast.state === 'complete' ? 'Broadcast complete' : 'Broadcast progress'} · {settled} / {broadcast.total}</Typography>
          <LinearProgress variant="determinate" value={broadcast.total ? settled / broadcast.total * 100 : 0} sx={{ my: 1.5, height: 7, borderRadius: 2 }} />
          <Typography variant="body2">{broadcast.accepted} accepted by the email provider · {broadcast.skipped} no longer eligible · {broadcast.unconfirmed} unconfirmed</Typography>
          {broadcast.unconfirmed > 0 && <Alert severity="warning" sx={{ mt: 1 }}>Some deliveries could not be confirmed. They will not be resent automatically, to avoid duplicate emails.</Alert>}
          {broadcast.state !== 'complete' && <Typography variant="caption" color="text.secondary">Keep this window open to continue sending. Closing it pauses after the current batch; you can resume here.</Typography>}
        </Box>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
      <Button disabled={busy} onClick={onClose}>Close</Button>
      <Box sx={{ flex: 1 }} />
      {preview && <Button variant="outlined" disabled={busy || !preview.delivery.ready} onClick={sendTest}>Send test to me</Button>}
      {sending ? <Button variant="outlined" onClick={() => { stop.current = true; setNotice('Pausing after the current batch.'); }}>Pause after this batch</Button> :
        broadcast?.state !== 'complete' && <Button variant="contained" startIcon={<SendOutlined />} disabled={busy || (!broadcast && (!preview?.delivery.ready || !preview.count || !confirmed))}
          onClick={send}>{broadcast ? 'Resume sending' : `Send to ${preview?.count || 0} researchers`}</Button>}
    </DialogActions>
  </Dialog>;
}
